import { Sortable } from '@shopify/draggable';

// Panels can be dragged between the two columns and the arrangement is kept in
// local storage. This is deliberately in local storage for now as it's not needed in
// the db yet.
const STORAGE_KEY = 'reporting:panel-layout:v1';

let sortable = null;

// Local storage throws in private browsing on some browsers, and the stored
// value can be anything if it was hand edited, so both ends are guarded. A bad
// value just means the server order stands.
const readLayout = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    if (!Array.isArray(parsed)) {
      return null;
    }
    
    return parsed.every(column => Array.isArray(column)) ? parsed : null;
  } catch (e) {
    return null;
  }
};

const writeLayout = (layout) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch (e) {
    // Not much to do here, the layout will be wiped when a reload happens.
    // This should not really affect the user so we don't inform them of it as they can't fix it.
  }
};

const clearLayout = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Not much to do here, the layout will be wiped when a reload happens.
    // This should not really affect the user so we don't inform them of it as they can't fix it.
  }
};

const panelKeys = (column) =>
  [...column.querySelectorAll('[data-panel-key]')].map(panel => panel.dataset.panelKey);

const readColumns = (columns) => columns.map(panelKeys);

// Keys the saved layout has never seen keep the column the server put them in,
// appended after the panels the user has arranged. That way a metric added
// later still appears instead of being silently dropped.
const applyLayout = (columns, saved, fallback) => {
  const panels = new Map();
  columns.forEach(column => {
    column.querySelectorAll('[data-panel-key]').forEach(panel => panels.set(panel.dataset.panelKey, panel));
  });

  const placed = new Set();
  const order = columns.map((_column, index) => (saved[index] || []).filter(key => panels.has(key)));

  order.forEach(keys => keys.forEach(key => placed.add(key)));
  fallback.forEach((keys, index) => {
    keys.filter(key => !placed.has(key)).forEach(key => {
      order[index].push(key);
      placed.add(key);
    });
  });

  order.forEach((keys, index) => {
    keys.forEach(key => columns[index].appendChild(panels.get(key)));
  });
};

const init = () => {
  const container = document.getElementById('reporting-panels');
  if (!container) {
    return;
  }

  const columns = [...container.querySelectorAll('.reporting-column')];
  if (!columns.length) {
    return;
  }

  if (sortable) {
    sortable.destroy();
  }

  const fallback = readColumns(columns);
  const saved = readLayout();
  if (saved) {
    applyLayout(columns, saved, fallback);
  }

  const resetButton = document.getElementById('reporting-reset-layout');
  if (resetButton) {
    resetButton.classList.toggle('hidden', !saved);
    resetButton.addEventListener('click', () => {
      clearLayout();
      applyLayout(columns, fallback, fallback);
      resetButton.classList.add('hidden');
    });
  }

  sortable = new Sortable(columns, {
    draggable: '.reporting-panel',
    handle: '.reporting-drag-handle',
    // Without this the mirror is appended to the body with no dimensions and
    // the panel collapses to a squashed header while in flight.
    mirror: {constrainDimensions: true}
  });

  sortable.on('sortable:start', () => container.classList.add('reporting-panels--dragging'));

  sortable.on('sortable:stop', () => {
    container.classList.remove('reporting-panels--dragging');
    // sortable:stop fires before the panel is settled in its new home, so the
    // order is read on the next frame.
    requestAnimationFrame(() => {
      writeLayout(readColumns(columns));
      if (resetButton) {
        resetButton.classList.remove('hidden');
      }
    });
  });
};

document.addEventListener('turbo:load', () => {
  const frame = document.getElementById('reporting_metrics');
  if (!frame) {
    return;
  }

  // The panels arrive in a lazy frame, so they are usually not in the document
  // yet on turbo:load. Listening on the frame itself avoids depending on
  // whether turbo:frame-load bubbles.
  frame.addEventListener('turbo:frame-load', init);
  init();
});
